import styled, { css } from "styled-components";

import Heading from "../Heading";

import { default as breakpoints } from "./breakpoints";
import { colors } from "./colors";
import { easingSeconds } from "./constants";
import { default as fonts } from "./fonts";
import { regular as regularFontWeight } from "./fontStyles";
import { spacing2020HasBreakpoints } from "./spacing";
import { P } from "./Typography";

const browserIsIE = window.document.documentMode;

/* general card components */
export const CardIconContainer = styled.div`
    display: inline-block;

    svg {
        height: 2.0625rem;
        width: auto;

        path {
            fill: ${colors.dark};
        }
    }
`;

export const CardTitle = styled(Heading)``;

export const CardDescription = styled.p`
    ${regularFontWeight}

    /* Tablet: */
    @media (min-width: ${breakpoints.md}rem) {
        font-size: 1rem;
        line-height: 1.5rem;
    }

    /* Desktop: */
    @media (min-width: ${breakpoints.lg}rem) {
        font-size: 1.125rem;
        line-height: 1.8125rem;
    }
`;

export const CardHighlightsContainer = styled.ul`
    text-align: left;

    /* Mobile and tablet: */
    @media (max-width: ${breakpoints.beforeDesktop}rem) {
        display: none;
    }
`;

export const CardHighlight = styled.li`
    font-size: 0.9rem; /* 14.4px custom */
    color: ${colors.brownishGrey};

    &:not(:last-of-type) {
        padding-bottom: 0.375rem;
        border-bottom: 1px solid ${colors.veryLightPinkTwoDuplicate};
    }

    &:not(:first-of-type) {
        padding-top: 0.625rem;
    }
`;

export const CardCta = styled.a`
    && {
        @media (min-width: ${breakpoints.md}rem) {
            ${regularFontWeight}
            display: inline-block;
            background-color: transparent;
            width: 100%;
            font-size: 0.9375rem; /* 15px custom */
            border: 2px solid ${colors.veryLightPinkThree};
            padding: 0.875rem;
            text-align: center;
            border-radius: 4px;
            color: ${colors.dark};
            cursor: pointer;
            transition: all ${easingSeconds};
            text-decoration: none;

            &:hover {
                border: 2px solid ${colors.veryLightPinkTwo};
                background-color: ${colors.darkModeLink};
            }

            &:active {
                border: 2px solid ${colors.marketingCtaPressed};
                background-color: ${colors.marketingCtaPressed};
            }

            &:focus {
                background-color: ${colors.whiteDuplicate};
            }
        }
    }
`;

export const CardNonMobileContentContainer = styled.div`
    ${Card} & {
        & > *:not(:last-child) {
            ${spacing2020HasBreakpoints.SpacingLG};
        }

        @media (max-width: ${breakpoints.beforeTablet}rem) {
            display: none;
        }
    }
`;

export const CardMobileContentContainer = styled.div`
    ${Card} & {
        display: flex;
        align-items: center;
        text-align: left;
        line-height: 1.3rem;

        @media (min-width: ${breakpoints.md}rem) {
            display: none;
        }

        & > *:not(:last-child) {
            margin-right: 1rem;
        }

        ${CardIconContainer} {
            width: 2.8125rem;
            height: 2.8125rem;
        }

        ${CardCta} {
            border: none;
            font-size: 0.875rem;
            text-decoration: underline;
            cursor: pointer;
            padding: 0;
            color: ${colors.brownishGrey};
        }
    }
`;

const bulletListCardStyles = css`
    padding: 1.5rem;
    text-align: left;

    @media (max-width: ${breakpoints.beforeTablet}rem) {
        padding-right: 0;
    }
`;

/* card container component */
export const Card = styled.div`
    border: 1px solid
        ${({ darkMode }) => {
            return darkMode
                ? colors.cardDarkModeBorder
                : colors.veryLightPinkTwoDuplicate;
        }};
    text-align: center;
    color: ${colors.dark};
    background-color: ${({ darkMode, transparent }) => {
        if (transparent) return "transparent";
        return darkMode ? colors.dark : colors.whiteDuplicate;
    }};

    padding: 1rem;

    /* Tablet: */
    @media (min-width: ${breakpoints.md}rem) {
        padding: 2.5rem 0.875rem;
    }

    /* Desktop: */
    @media (min-width: ${breakpoints.lg}rem) {
        padding: 3rem 1.875rem;
    }

    /* applies padding for bullet list style
    cards on all screen sizes */
    @media (min-width: ${breakpoints.sm}rem) {
        ${({ bulletList }) => bulletList && bulletListCardStyles}
    }
`;

/* bullet list card components */
export const CardBulletListTitle = styled(P).attrs({ bold: true })`
    font-size: 0.9rem; /* 14.4px custom */
`;

export const CardBulletListTitleIcon = styled.div`
    display: flex;
    align-items: center;
    width: 2rem;

    svg {
        width: 100%;
        height: auto;

        ${browserIsIE && "height: 28px;"}
    }
`;

export const CardBulletListTitleContainer = styled.div`
    display: flex;
    align-items: center;
    ${spacing2020HasBreakpoints.SpacingLG}

    ${CardBulletListTitleIcon} {
        margin-right: 1rem;
    }
`;

export const CardBulletList = styled.ul`
    list-style-type: disc;
    ${fonts.typeScale[12]};
    margin-left: 1.75rem;

    li {
        padding-left: 1.25rem;

        &:not(:last-of-type) {
            padding-bottom: 0.875rem; /* 14px custom */
        }
    }
`;
